/**
 * Google Workspace sign-in, for clients with no Microsoft Entra tenant.
 *
 * The browser never talks to Google here. The backend owns the client secret,
 * the PKCE verifier and the refresh token (see backend
 * routers/google_auth.py), because Google gives browser clients no reliable
 * silent renewal. This module holds two things: an opaque session handle and
 * the current Google ID token, which IS the Bearer token the API expects.
 *
 * See docs/google-workspace-sso-plan-2026-08-25.md.
 */

const API_BASE = import.meta.env.VITE_API_BASE;

const HANDLE_KEY = "iq_google_session";
const TOKEN_KEY = "iq_google_id_token";
const ERROR_KEY = "iq_google_signin_error";

/**
 * Renew this many milliseconds BEFORE the ID token actually expires.
 *
 * Google ID tokens last an hour. Renewing on expiry rather than ahead of it
 * would let a request leave the browser with a token that expires in flight,
 * which the API rejects as 401 and the user experiences as a random failure.
 */
const RENEW_SKEW_MS = 120_000;

export type GoogleAccount = {
  provider: "google";
  email: string;
  name: string;
};

/** localStorage, wrapped: it throws outright in some privacy modes. */
function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* private mode: the session simply will not survive a reload */
  }
}

function drop(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* no-op */
  }
}

/**
 * Claims out of an ID token, WITHOUT verifying it.
 *
 * Safe only because nothing here is a security decision: the backend validates
 * every token against Google's JWKS on every request, and this reads `exp` to
 * decide when to renew plus `email`/`name` to render a nameplate. A tampered
 * token would simply be rejected by the API.
 */
function claimsOf(token: string): Record<string, unknown> | null {
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function expiryOf(token: string): number {
  const exp = claimsOf(token)?.exp;
  return typeof exp === "number" ? exp * 1000 : 0;
}

let account: GoogleAccount | null = null;

function accountFrom(token: string): GoogleAccount | null {
  const claims = claimsOf(token);
  if (!claims) return null;
  const email = typeof claims.email === "string" ? claims.email : "";
  const name = typeof claims.name === "string" ? claims.name : email;
  if (!email) return null;
  return { provider: "google", email, name };
}

/** True when a Google session is present in this browser. */
export function hasSession(): boolean {
  return read(HANDLE_KEY) !== null && read(TOKEN_KEY) !== null;
}

export function currentAccount(): GoogleAccount | null {
  return account;
}

/** The path the backend redirects back to after a Google sign-in. */
export const CALLBACK_PATH = "/auth/google/callback";

export function isCallbackUrl(): boolean {
  return window.location.pathname === CALLBACK_PATH;
}

/** Begin sign-in. Leaves the page. */
export function startSignIn(): void {
  // return_origin is validated server-side against an allow-list and stored in
  // the state record, so staging and prod can share one backend without a
  // hardcoded redirect sending every staging tester to prod.
  const params = new URLSearchParams({ return_origin: window.location.origin });
  const hint = import.meta.env.VITE_GOOGLE_HD_HINT;
  // Pre-scopes Google's account chooser to the client's domain. A hint only:
  // the real gate is the signed `hd` claim, checked by the backend.
  if (hint) params.set("hd", hint);
  window.location.assign(`${API_BASE}/api/auth/google/start?${params.toString()}`);
}

/**
 * Consume the `#s=<handle>&t=<id_token>` (or `#error=<reason>`) fragment the
 * backend redirected back with.
 *
 * Returns true when a session was established. The fragment is stripped from
 * history either way, so a reload cannot replay it and the token never sits in
 * a URL the user might copy.
 */
export function consumeCallback(): boolean {
  const fragment = window.location.hash.replace(/^#/, "");
  // Clear FIRST, so an exception below cannot leave a token in the address bar.
  window.history.replaceState(null, "", "/");
  if (!fragment) return false;

  const params = new URLSearchParams(fragment);
  const error = params.get("error");
  if (error) {
    write(ERROR_KEY, error);
    return false;
  }

  const handle = params.get("s");
  const token = params.get("t");
  if (!handle || !token) return false;

  write(HANDLE_KEY, handle);
  write(TOKEN_KEY, token);
  drop(ERROR_KEY);
  account = accountFrom(token);
  return account !== null;
}

/**
 * Restore a session stored by an earlier visit.
 *
 * Renews before returning when the stored token is at or near expiry, which is
 * the common case: the token lives an hour and the tab was probably closed
 * overnight. Booting on a stale token instead would render the whole app shell
 * and only then discover the session was dead on the first API call, so the
 * user would watch the portal appear and then bounce back to sign-in.
 */
export async function restore(): Promise<boolean> {
  const token = read(TOKEN_KEY);
  if (!read(HANDLE_KEY) || !token) return false;
  account = accountFrom(token);
  if (account === null) {
    // A stored token we cannot even parse is not a session.
    signOut();
    return false;
  }
  try {
    // Returns the cached token untouched when it is still fresh.
    await getAccessToken();
  } catch {
    // Dead session, or Google unreachable. Either way there is nothing to
    // restore; the sign-in page is the honest answer.
    signOut();
    return false;
  }
  return account !== null;
}

/** Why the last sign-in attempt failed, if it did. Cleared once read. */
export function takeSignInError(): string | null {
  const reason = read(ERROR_KEY);
  if (reason) drop(ERROR_KEY);
  return reason;
}

export function signOut(): void {
  drop(HANDLE_KEY);
  drop(TOKEN_KEY);
  account = null;
}

/**
 * A single in-flight renewal, shared by every caller.
 *
 * This is load-bearing, not an optimisation. The backend ROTATES the session
 * handle on every successful refresh, so two concurrent renewals would each
 * present the same handle: the first rotates it, the second then presents a
 * handle that no longer exists and is told the session is invalid. Since the
 * portal fires several API calls on page load, an unsynchronised renewal would
 * sign the user out roughly whenever their token expired mid-load.
 */
let inFlight: Promise<string> | null = null;

async function renew(): Promise<string> {
  const handle = read(HANDLE_KEY);
  if (!handle) throw new Error("Not signed in");

  const r = await fetch(`${API_BASE}/api/auth/google/refresh`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ session: handle }),
  });

  if (r.status === 401) {
    // The session is genuinely over: revoked at Google, expired, or the handle
    // is stale. Clear it and RETURN TO THE SIGN-IN PAGE, which is what the MSAL
    // provider does in the same situation (acquireTokenRedirect navigates
    // away). Throwing alone would leave the app shell rendered around a session
    // that can no longer make a single call, surfacing as an unexplained error
    // in whichever component happened to fetch first.
    signOut();
    write(ERROR_KEY, "session_expired");
    window.location.assign("/");
    throw new Error("Google session has ended");
  }
  if (!r.ok) {
    // A transient upstream problem (the backend answers 502 for those). Keep
    // the session: the next call retries, and signing the user out over a
    // Google blip would be the wrong trade.
    throw new Error(`Could not renew the Google session (${r.status})`);
  }

  const body = (await r.json()) as { id_token: string; session: string };
  // Store the rotated handle and the fresh token together. If this write is
  // interrupted the session is lost, which is recoverable by signing in again;
  // storing them in the other order could leave a live handle paired with a
  // dead token.
  write(HANDLE_KEY, body.session);
  write(TOKEN_KEY, body.id_token);
  account = accountFrom(body.id_token) ?? account;
  return body.id_token;
}

/** The current Google ID token, renewed through the backend when near expiry. */
export async function getAccessToken(): Promise<string> {
  const token = read(TOKEN_KEY);
  if (!token) throw new Error("Not signed in");
  if (Date.now() < expiryOf(token) - RENEW_SKEW_MS) return token;

  if (!inFlight) {
    inFlight = renew().finally(() => {
      inFlight = null;
    });
  }
  return inFlight;
}
